// components/emoji/emojiPicker.js

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import "./emoji.styles.scss";
import Picker from "@emoji-mart/react";

export default function EmojiPicker({
  onEmojiSelect,
  onClickOutside,
  embedded = false,
  autoFocus = true,
  floating = false,
  anchorRef = null,
}) {
  const container = useRef(null);
  const [grid, setGrid] = useState({ columns: 7, size: 36 });
  const [themeName, setThemeName] = useState("light");

  const floatingRef = useRef(null);

const [floatingPosition, setFloatingPosition] = useState(null);

  const theme = useSelector((state) => state.theme.theme);
  const location = useLocation();

  useEffect(() => {
    setThemeName(theme === "dark-theme" ? "dark" : "light");
  }, [theme, location]);

  useEffect(() => {
    if (!embedded || !container.current) return;

    const observer = new ResizeObserver(([entry]) => {
      const available = Math.max(36, entry.contentRect.width - 28);
      const columns = Math.max(1, Math.floor(available / 36));
      const size = Math.floor(available / columns);

      setGrid((current) =>
        current.columns === columns && current.size === size
          ? current
          : { columns, size },
      );
    });

    observer.observe(container.current);
    return () => observer.disconnect();
  }, [embedded]);

  useEffect(() => {
  if (!floating || !anchorRef?.current) return;

  const updatePosition = () => {
    const anchor = anchorRef.current?.getBoundingClientRect();
    const picker = floatingRef.current?.getBoundingClientRect();

    if (!anchor || !picker) return;

    const gap = 8;
    const pad = 10;

    let left = anchor.left;
    let top = anchor.bottom + gap;

    // Keep it inside the right side of the viewport.
    left = Math.min(
      Math.max(pad, left),
      window.innerWidth - picker.width - pad,
    );

    // If there isn't enough room below, open above instead.
    if (top + picker.height > window.innerHeight - pad) {
      top = anchor.top - picker.height - gap;
    }

    setFloatingPosition({
      top: Math.max(pad, top),
      left,
    });
  };

  requestAnimationFrame(updatePosition);

  window.addEventListener("resize", updatePosition);
  window.addEventListener("scroll", updatePosition, true);

  return () => {
    window.removeEventListener("resize", updatePosition);
    window.removeEventListener("scroll", updatePosition, true);
  };
}, [floating, anchorRef]);

  const picker = (
    <>
      <style>{`em-emoji-picker { --rgb-accent: 26, 130, 196; }`}</style>

      <Picker
        native
        theme={themeName}
        showPreview={false}
        showSkinTones
        emojiTooltip
        autoFocus={autoFocus}
        perLine={embedded ? grid.columns : 7}
        emojiButtonSize={embedded ? grid.size : 36}
        previewPosition={embedded ? "none" : "bottom"}
        color="#1a82c4"
        sheetSize={15}
        style={{
          position: embedded || floating ? "relative" : "absolute",
          width: embedded ? "100%" : undefined,
          maxWidth: embedded ? "100%" : undefined,
          maxHeight: embedded ? "310px" : "250px",
          outline: "none",
        }}
        onEmojiSelect={onEmojiSelect}
        onClickOutside={embedded ? undefined : onClickOutside}
        skinTonePosition="search"
      />
    </>
  );

  if (embedded) {
    return <div ref={container}>{picker}</div>;
  }

if (floating) {
  return createPortal(
    <div
      ref={floatingRef}
      className="EmojiPickerFloating"
      style={
        anchorRef
          ? {
              top: floatingPosition?.top ?? 0,
              left: floatingPosition?.left ?? 0,
              transform: "none",
              visibility: floatingPosition ? "visible" : "hidden",
            }
          : undefined
      }
    >
      {picker}
    </div>,
    document.body,
  );
}

  return picker;
}